import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { mediaAPI } from '../../services/api';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { formatDate, handleApiError, getImageUrl, showToast, showConfirm } from '../../utils/helpers';
import MediaTagsModal from './MediaTagsModal';

const MediaPreviewModal = ({ media, onClose, onUpdate, onDelete }) => {
  const { isRTL, isArabic } = useLanguage();
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من حذف هذا الملف؟' : 'Are you sure you want to delete this file?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setDeleting(true);
      await mediaAPI.delete(media.id);
      if (onDelete) {
        onDelete(media.id);
      }
      showToast.success(isRTL ? 'تم حذف الملف بنجاح!' : 'File deleted successfully!');
      onClose();
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-bold text-gray-900 truncate flex-1">
              {media.file_name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-3xl leading-none ml-4"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Media Preview */}
              <div className="lg:col-span-2">
                <div className="bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center min-h-[400px]">
                  {media.file_type === 'image' ? (
                    <img
                      src={getImageUrl(media.file_url)}
                      alt={media.file_name}
                      className="max-w-full max-h-[70vh] object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="text-center p-8"><span class="text-6xl">🖼️</span><p class="text-gray-500 mt-4">Image not found</p></div>';
                      }}
                    />
                  ) : (
                    <div className="text-center p-8">
                      <span className="text-6xl">🎬</span>
                      <p className="text-gray-500 mt-4">{isRTL ? 'معاينة الفيديو غير متاحة' : 'Video preview not available'}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Media Info */}
              <div className="lg:col-span-1 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    {isRTL ? 'معلومات الملف' : 'File Information'}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">{isRTL ? 'الاسم:' : 'Name:'}</span>
                      <p className="font-medium text-gray-900">{media.file_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">{isRTL ? 'النوع:' : 'Type:'}</span>
                      <p className="font-medium text-gray-900 capitalize">{media.file_type}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">{isRTL ? 'الحجم:' : 'Size:'}</span>
                      <p className="font-medium text-gray-900">{formatFileSize(media.file_size)}</p>
                    </div>
                    {media.width && media.height && (
                      <div>
                        <span className="text-gray-500">{isRTL ? 'الأبعاد:' : 'Dimensions:'}</span>
                        <p className="font-medium text-gray-900">{media.width} × {media.height} px</p>
                      </div>
                    )}
                    {media.mime_type && (
                      <div>
                        <span className="text-gray-500">{isRTL ? 'نوع MIME:' : 'MIME Type:'}</span>
                        <p className="font-medium text-gray-900">{media.mime_type}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">{isRTL ? 'تاريخ الرفع:' : 'Uploaded:'}</span>
                      <p className="font-medium text-gray-900">{formatDate(media.created_at)}</p>
                    </div>
                    {media.uploaded_by_name && (
                      <div>
                        <span className="text-gray-500">{isRTL ? 'رفع بواسطة:' : 'Uploaded by:'}</span>
                        <p className="font-medium text-gray-900">{media.uploaded_by_name}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {media.description && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      {isRTL ? 'الوصف' : 'Description'}
                    </h3>
                    <p className="text-sm text-gray-900">{media.description}</p>
                  </div>
                )}

                {/* Tags */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">
                      {isRTL ? 'الوسوم' : 'Tags'}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTagsModal(true)}
                    >
                      {isRTL ? 'تعديل' : 'Edit'}
                    </Button>
                  </div>
                  {media.tags && media.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {media.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-primary-100 text-primary-800 rounded text-xs font-medium"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">{isRTL ? 'لا توجد وسوم' : 'No tags'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-4 border-t">
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleting}
              disabled={deleting}
            >
              {isRTL ? 'حذف' : 'Delete'}
            </Button>
            <Button
              variant="secondary"
              onClick={onClose}
            >
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </div>
        </div>
      </div>

      {/* Tags Modal */}
      {showTagsModal && (
        <MediaTagsModal
          media={media}
          onClose={() => setShowTagsModal(false)}
          onSuccess={(updatedMedia) => {
            setShowTagsModal(false);
            if (onUpdate) {
              onUpdate(updatedMedia);
            }
          }}
        />
      )}
    </>
  );
};

export default MediaPreviewModal;




